import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { ProductSales } from '@domain/sales/models/Sales';
import { PaginatedResource, Resource } from '@domain/base/Resource';
import { ProductFilter } from '@domain/product/models/ProductFilter';

interface UseSalesOptions {
  productIds?: string[];
  startDate?: Date;
  endDate?: Date;
  filterCriteria?: ProductFilter;
  marketplaces?: string[];
  countries?: string[];
  enabled?: boolean;
}

export function useSales({
  startDate,
  endDate,
  filterCriteria,
  marketplaces,
  countries,
  enabled = true,
}: UseSalesOptions) {
  const {
    data: sales,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['sales', startDate?.toISOString(), endDate?.toISOString(), filterCriteria, marketplaces, countries],
    queryFn: async (): Promise<PaginatedResource<ProductSales[]>> => {
      return container.salesRepository.getWeeklySales(startDate, endDate, filterCriteria, marketplaces, countries);
    },
    enabled,
  });


  return {
    sales,
    isLoading: isLoading,
    error,
  };
}

export function useProductSales(productId?: string, startDate?: Date, endDate?: Date) {
  const queryClient = useQueryClient();
  
  const {
    data: sales,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['sales', productId, startDate?.toISOString(), endDate?.toISOString()],
    queryFn: async (): Promise<Resource<ProductSales>> => {
      return container.salesRepository.getProductSales(productId, startDate, endDate);
    },
    enabled: productId !== undefined,
  });

  const { mutateAsync: updateWeeklySalesAnomalies, isPending: isUpdatingWeeklySalesAnomalies } = useMutation({
    mutationFn: async (weeks: { week: number, year: number, anomaly: boolean }[]) => {
      if (!productId) throw new Error('Product ID is required');
      return container.salesRepository.updateWeeklySalesAnomalies(productId, weeks);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', productId, startDate?.toISOString(), endDate?.toISOString()] });
    }
  });

  const { mutateAsync: clearWeeklySalesAnomalies, isPending: isClearingWeeklySalesAnomalies } = useMutation({
    mutationFn: async (weeks: { week: number, year: number }[]) => {
      if (!productId) throw new Error('Product ID is required');
      return container.salesRepository.clearWeeklySalesAnomalies(productId, weeks);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', productId, startDate?.toISOString(), endDate?.toISOString()] });
    }
  });

  return {
    sales,
    isLoading,
    isUpdatingWeeklySalesAnomalies,
    isClearingWeeklySalesAnomalies,
    error,
    updateWeeklySalesAnomalies,
    clearWeeklySalesAnomalies,
  };
}